import { PageErrorBoundary } from "@/components/error-boundary";
import { SessionExpiredModal } from "@/components/modals/session-expired-modal";
import "@/features/templates/template-styles.css";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import "@/styles/globals.scss";
import { AccessProvider } from "@/utils/access-provider";
import { ApiSchemaProvider } from "@/utils/api-schema-provider";
import { LanguageProvider } from "@/utils/language-provider";
import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { Toaster } from "sonner";
import { AppRoutes } from "./routes";

config.autoAddCss = false;

dayjs.extend(minMax);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
    },
    mutations: {
      networkMode: "always",
    },
  },
});

/**
 * Component that applies workspace theme colors to CSS variables.
 */
const WorkspaceThemeApplier: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useWorkspaceTheme();
  return <>{children}</>;
};

const App: React.FC = () => {
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      }
    }
  }, []);

  return (
    <React.StrictMode>
      <PageErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <BrowserRouter>
              <AccessProvider>
                <ApiSchemaProvider>
                  <WorkspaceThemeApplier>
                    <Toaster
                      position="bottom-right"
                      gap={8}
                      toastOptions={{
                        unstyled: true,
                        duration: 4000,
                      }}
                    />
                    <SessionExpiredModal />
                    <AppRoutes />
                  </WorkspaceThemeApplier>
                </ApiSchemaProvider>
              </AccessProvider>
            </BrowserRouter>
          </LanguageProvider>
        </QueryClientProvider>
      </PageErrorBoundary>
    </React.StrictMode>
  );
};

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
